// Stock in other stores for one style and colour, by size, with a transfer
// request for the size the customer wants (endless aisle). Online only: the
// figures come live from getProductsNearbyStock, one call per size of the
// chosen colour, less what each store has already promised to holds and online
// orders (getFashionStockCommitments). "Hold there" keeps the pieces back at
// the other store for the customer, then asks for them to be sent here; it is
// offered only while holds are switched on (FASHION_FEATURES, or `canHold`).
// With `size` set (the floor's product screen, once a size is picked) it lists
// one row per store for just that size instead of the store × size grid.
import React, { useEffect, useMemo, useRef, useState } from "react";
import { fashionPosService, getProductsNearbyStockService, generateTransferRequestService } from "../../../services";
import { findVariant } from "../../../utils/fashion/styleIndex";
import {
  buildNearbyStockRequest,
  parseNearbyStores,
  buildStockGrid,
  buildTransferRequestMutation,
  transferQtyError,
  readTransferResult,
  localStockLabel,
  buildStockCommitmentsQuery,
  readStockCommitments,
  freeStock,
} from "../../../utils/fashion/storeStock";
import { buildCreateHoldMutation, defaultHeldUntil, readMessage, validateHoldDraft } from "../../../utils/fashion/holds";
import { FASHION_FEATURES } from "../../../utils/fashion/features";
import EmptyState from "./EmptyState";

async function loadCommitments(productIds) {
  try {
    return readStockCommitments(await fashionPosService(buildStockCommitmentsQuery(productIds)));
  } catch {
    return null;
  }
}

const sizeKey = (s) => s ?? "One size";

// `customer` is the bill's named customer, if any, to fill in a hold.
export default function FashionStockPanel({ style, tillData, isOnline, initialColour, customer, size: onlySize, canHold = FASHION_FEATURES.holds }) {
  const listMode = onlySize !== undefined;
  const [colour, setColour] = useState(initialColour ?? style.colours[0] ?? null);
  const [state, setState] = useState({ status: "idle", grid: [], failed: 0 });
  const [pick, setPick] = useState(null); // { store, size, variant }
  const [qty, setQty] = useState("1");
  const [sendState, setSendState] = useState({ status: "idle", message: "" });
  const [holding, setHolding] = useState(null); // { customerName, mobileNo } while the hold form is open
  const request = useRef(0);
  const online = typeof isOnline === "boolean" ? isOnline : typeof navigator === "undefined" || navigator.onLine;

  const sizes = useMemo(() => {
    const list = style.sizes.length > 0 ? style.sizes : [null];
    return list
      .filter((s) => !listMode || s === onlySize)
      .map((s) => ({ size: s, variant: findVariant(style, colour, s) }))
      .filter((x) => x.variant);
  }, [style, colour, listMode, onlySize]);

  useEffect(() => {
    setPick(null);
    setHolding(null);
    setSendState({ status: "idle", message: "" });
    if (!online || sizes.length === 0) return;
    const id = ++request.current;
    setState({ status: "loading", grid: [], failed: 0 });
    Promise.all([
      Promise.all(
        sizes.map(({ size, variant }) =>
          getProductsNearbyStockService(buildNearbyStockRequest(tillData, variant.value))
            .then((res) => ({ size: sizeKey(size), stores: parseNearbyStores(res, tillData) }))
            .catch(() => ({ size: sizeKey(size), stores: null }))
        )
      ),
      loadCommitments(sizes.map(({ variant }) => variant.mProductId)),
    ]).then(([perSize, commitments]) => {
      if (id !== request.current) return;
      const failed = perSize.filter((p) => p.stores === null).length;
      setState({ status: failed === perSize.length ? "error" : "done", grid: buildStockGrid(perSize.filter((p) => p.stores)), failed, commitments });
    });
  }, [sizes, online]); // eslint-disable-line react-hooks/exhaustive-deps

  const send = async (e) => {
    e.preventDefault();
    if (!pick || sendState.status === "sending") return;
    const error = transferQtyError(qty, pick.store);
    if (error) {
      setSendState({ status: "error", message: error });
      return;
    }
    setSendState({ status: "sending", message: "" });
    const result = await requestTransfer();
    setSendState(
      result.ok
        ? { status: "sent", message: `Requested ${qty} × ${pick.size} from ${pick.store.name}.${result.recordId ? ` Request ${result.recordId}.` : ""}` }
        : { status: "error", message: result.message }
    );
  };

  // Hold the pieces at the other store first, so they aren't sold there while
  // the transfer is on its way, then request them. A hold that went through
  // stays even when the request fails; the message says so.
  const holdThere = async (e) => {
    e.preventDefault();
    if (!pick || sendState.status === "sending") return;
    const error = transferQtyError(qty, pick.store);
    if (error) {
      setSendState({ status: "error", message: error });
      return;
    }
    const here = tillData?.tillAccess?.csBunit;
    const draft = validateHoldDraft({
      ...holding,
      qty,
      productId: pick.variant.mProductId,
      customerId: customer?.cwrCustomerId || null,
      heldUntil: defaultHeldUntil(),
      note: `For the customer at ${here?.name || "another store"}. Send it there, then release this hold.`,
    });
    if (draft.error) {
      setSendState({ status: "error", message: draft.error });
      return;
    }
    setSendState({ status: "sending", message: "" });
    let held;
    try {
      held = readMessage(await fashionPosService(buildCreateHoldMutation({ ...draft.hold, bunitId: pick.store.bunitId })), "createFashionHold", "Couldn't hold it there. Try again.");
    } catch {
      held = { ok: false, message: "Couldn't hold it there. Check the connection and try again." };
    }
    if (!held.ok) {
      setSendState({ status: "error", message: held.message });
      return;
    }
    const heldText = `Held ${qty} × ${pick.size} at ${pick.store.name}${held.documentNo ? ` (${held.documentNo})` : ""}`;
    const result = await requestTransfer();
    setSendState(
      result.ok
        ? { status: "sent", message: `${heldText} and asked for it here.${result.recordId ? ` Request ${result.recordId}.` : ""}` }
        : { status: "sent", error: true, message: `${heldText}, but the transfer request didn't go: ${result.message} Ask ${pick.store.name} to send it.` }
    );
  };

  const requestTransfer = async () => {
    const here = tillData?.tillAccess;
    const mutation = buildTransferRequestMutation({
      fromBunitId: pick.store.bunitId,
      toBunitId: here?.csBunit?.csBunitId,
      userId: here?.csUserId,
      lines: [{ productId: pick.variant.mProductId, qty: Number(qty) }],
      description: `Fashion POS request: ${pick.variant.name || style.name}`,
    });
    try {
      return readTransferResult(await generateTransferRequestService({ query: mutation }));
    } catch {
      return readTransferResult(null);
    }
  };

  const choose = (store, key, variant, free, hold) => {
    setPick({ store: { ...store, qty: free }, size: key, variant });
    setQty("1");
    setHolding(hold ? { customerName: customer?.name || "", mobileNo: customer?.mobileNo || "" } : null);
    setSendState({ status: "idle", message: "" });
  };

  const busy = sendState.status === "sending";
  const done = sendState.status === "sent";

  if (!online) return <EmptyState icon="offline" title="You're offline" hint="Other stores' stock needs a connection. Reconnect the till and try again." />;

  return (
    <div className="fp-stock">
      {!listMode && style.colours.length > 1 && (
        <div className="fp-row" role="group" aria-label="Colour">
          {style.colours.map((c) => (
            <button key={c} type="button" className={`fp-chip${c === colour ? " is-on" : ""}`} onClick={() => setColour(c)}>
              {c}
            </button>
          ))}
        </div>
      )}
      {sizes.length === 0 ? (
        <EmptyState compact title={`This style isn't made in ${colour}.`} />
      ) : listMode ? (
        state.grid.length > 0 && (
          <ul className="fp-stock-list" aria-label={`Other stores, size ${sizeKey(onlySize)}`}>
            {state.grid.map((row) => {
              const { size: s, variant } = sizes[0];
              const key = sizeKey(s);
              const { onHand, free, promised } = freeStock(row.cells[key] ?? 0, state.commitments, row.store.bunitId, variant.mProductId);
              const store = row.stores[key];
              const can = free > 0 && Boolean(store?.canIssue);
              const on = pick && pick.store.bunitId === row.store.bunitId;
              return (
                <li key={row.store.bunitId} className={`fp-stock-store${on ? " is-on" : ""}`}>
                  <span className="fp-stock-store-name">
                    <b>{row.store.name}</b>
                    <span className="fp-muted fp-num">
                      {free > 0 ? `${free} available` : "None"}
                      {promised > 0 && onHand > 0 ? ` · ${Math.min(promised, onHand)} promised` : ""}
                      {!row.store.canIssue ? " · view only" : ""}
                    </span>
                  </span>
                  <span className="fp-stock-store-acts">
                    {can && canHold && (
                      <button type="button" className="fp-btn-ghost fp-btn-sm" aria-label={`Hold ${key} at ${row.store.name} for pickup`} onClick={() => choose(store, key, variant, free, true)}>
                        Hold for pickup
                      </button>
                    )}
                    {can && (
                      <button type="button" className="fp-btn-ghost fp-btn-sm" aria-label={`Request ${key} from ${row.store.name}`} onClick={() => choose(store, key, variant, free, false)}>
                        Request here
                      </button>
                    )}
                  </span>
                </li>
              );
            })}
          </ul>
        )
      ) : (
        <div className="fp-stock-scroll">
          <table className="fp-stock-table">
            <thead>
              <tr>
                <th scope="col">Store</th>
                {sizes.map(({ size }) => (
                  <th key={sizeKey(size)} scope="col" className="fp-num">
                    {sizeKey(size)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr className="fp-stock-here">
                <th scope="row">This store</th>
                {sizes.map(({ size, variant }) => (
                  <td key={sizeKey(size)} className="fp-num">
                    {localStockLabel(variant) === null ? "–" : Math.max(0, Number(variant.onhandQty))}
                  </td>
                ))}
              </tr>
              {state.grid.map((row) => (
                <tr key={row.store.bunitId}>
                  <th scope="row">
                    {row.store.name}
                    {!row.store.canIssue && <span className="fp-muted"> · view only</span>}
                  </th>
                  {sizes.map(({ size, variant }) => {
                    const key = sizeKey(size);
                    const { onHand, free, promised } = freeStock(row.cells[key] ?? 0, state.commitments, row.store.bunitId, variant.mProductId);
                    const store = row.stores[key];
                    const on = pick && pick.store.bunitId === row.store.bunitId && pick.size === key;
                    const label = promised > 0 ? `${free} free, ${Math.min(promised, onHand)} of ${onHand} promised` : `${onHand} in stock`;
                    return (
                      <td key={key} className="fp-num">
                        {free > 0 && store?.canIssue ? (
                          <button
                            type="button"
                            className={`fp-stock-cell${on ? " is-on" : ""}`}
                            aria-label={`Request ${key} from ${row.store.name}, ${label}`}
                            onClick={() => {
                              setPick({ store: { ...store, qty: free }, size: key, variant });
                              setQty("1");
                              setHolding(null);
                              setSendState({ status: "idle", message: "" });
                            }}
                          >
                            {free}
                          </button>
                        ) : (
                          <span className="fp-muted">{free}</span>
                        )}
                        {promised > 0 && onHand > 0 && (
                          <span className="fp-muted fp-stock-promised" title="Held or reserved for online orders">
                            {" "}
                            of {onHand}
                          </span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className="fp-muted" role="status" aria-live="polite">
        {state.status === "loading" && "Checking other stores…"}
        {state.status === "error" && "Couldn't load other stores' stock. Check the connection and try again."}
        {state.status === "done" && state.grid.length === 0 && `No other store has this ${listMode ? "size" : "style"} in stock.`}
        {state.status === "done" && state.failed > 0 && ` ${state.failed} size(s) couldn't be checked.`}
        {state.status === "done" && state.grid.length > 0 && state.commitments === null && " Holds and online orders there couldn't be checked, so some pieces may already be promised."}
        {state.status === "done" && state.grid.length > 0 && !pick && !listMode && (canHold ? "Pick a number to request that size from the store, or hold it there for the customer." : "Pick a number to request that size from the store.")}
      </p>
      {pick && (
        <form className="fp-stock-req" onSubmit={holding ? holdThere : send} aria-label={`Request ${pick.size} from ${pick.store.name}`}>
          <span>
            {holding ? "Hold" : "Request"} <strong>{pick.size}</strong> {holding ? "at" : "from"} <strong>{pick.store.name}</strong>
          </span>
          <label className="fp-lbl fp-muted" htmlFor="fp-transfer-qty">
            Qty
          </label>
          <input id="fp-transfer-qty" className="fp-qty-input fp-num" inputMode="numeric" value={qty} onChange={(e) => setQty(e.target.value)} disabled={busy || done} />
          {holding && (
            <>
              <input
                className="fp-input fp-field-sm"
                value={holding.customerName}
                onChange={(e) => setHolding((h) => ({ ...h, customerName: e.target.value }))}
                placeholder="Customer name"
                aria-label="Customer name"
                autoComplete="off"
                disabled={busy || done}
              />
              <input
                className="fp-input fp-num fp-field-sm"
                value={holding.mobileNo}
                onChange={(e) => setHolding((h) => ({ ...h, mobileNo: e.target.value }))}
                placeholder="Mobile"
                aria-label="Mobile"
                inputMode="tel"
                autoComplete="off"
                disabled={busy || done}
              />
            </>
          )}
          <button type="submit" className="fp-btn-pay fp-btn-sm" disabled={busy || done}>
            {busy ? "Sending…" : holding ? "Hold and request" : "Send request"}
          </button>
          {!done && canHold && (
            <button
              type="button"
              className="fp-btn-ghost fp-btn-sm"
              disabled={busy}
              onClick={() => setHolding((h) => (h ? null : { customerName: customer?.name || "", mobileNo: customer?.mobileNo || "" }))}
            >
              {holding ? "Just request" : "Hold there for customer"}
            </button>
          )}
        </form>
      )}
      {sendState.message && (
        <p className={`fp-line-msg${sendState.status === "error" || sendState.error ? " is-err" : ""}`} role={sendState.status === "error" || sendState.error ? "alert" : "status"}>
          {sendState.message}
        </p>
      )}
    </div>
  );
}
